import React, { useState, useMemo, useEffect } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { UserPlus } from 'lucide-react';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useAllTenantUsers, useSearchUsers } from '../../hooks/useSAMUsers';
import type { SAMUser } from '../../types/role.types';
import { TableContent } from '../LayoutPage';
import { useBusinessContext } from '../../context/BusinessContext';
import { CreateUserDrawer } from '../../components/CreateUserDrawer';
import { AddBusinessUserSheet } from '../../components/AddBusinessUserSheet';
import { useBusinesses, type Business } from '../../hooks/useBusinessAccessType';
import { UserDetailDrawer } from '../../components/UserDetailDrawer';
import { AdminUsersPhone } from './AdminUsersPhone';
import { useIsPhone } from '../../hooks/useIsPhone';
import type { PhoneCapsule } from '../../components/phone/PhoneListParts';

// ── Styled components matching Users/Invitations/Estates ──

const Container = styled('div', {
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px 0 24px',
});

const ToolbarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 24px',
});

const SearchWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1',
  maxWidth: '280px',
  backgroundColor: '#EBEBEB',
  border: '1px solid #EBEBEB',
  borderRadius: '20px',
  padding: '7px 14px',
});

const SearchInputEl = styled('input', {
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  color: '#374151',
  flex: 1,
  width: '100%',
  '::placeholder': {
    color: '#9CA3AF',
  },
});

const Main = styled('div', {
  flex: 1,
  padding: '0 24px 24px 24px',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box' as any,
});

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
});

const StatusBadge = styled('span', ({ $status }: { $status: string }) => {
  const getColors = () => {
    switch ($status) {
      case 'CONFIRMED':
        return { bg: '#D1FAE5', color: '#065F46' };
      case 'FORCE_CHANGE_PASSWORD':
        return { bg: '#FEF3C7', color: '#92400E' };
      case 'UNCONFIRMED':
        return { bg: '#E5E7EB', color: '#374151' };
      default:
        return { bg: '#E5E7EB', color: '#374151' };
    }
  };

  const colors = getColors();

  return {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: colors.bg,
    color: colors.color,
  };
});

const GroupBadge = styled('span', {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: '#F0FDF4',
  color: '#166534',
  border: '1px solid #BBF7D0',
  marginRight: '4px',
  marginBottom: '4px',
});

const DisabledBadge = styled('span', {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '8px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: '#FEE2E2',
  color: '#991B1B',
  marginLeft: '6px',
});

// ── Component ──

/**
 * Cognito admin users (business_* groups). Lives as the "Admin Users" tab of
 * the Users page; app users are reviewed from the Users list itself, and their
 * Cognito attributes from the user detail's "App User Info" tab.
 */
export const AdminUsers = ({ embedded = false, phoneTabs }: {
  embedded?: boolean;
  /** SAM-1308: on a phone the Users tabs are the chips of this list (Users.tsx owns them). */
  phoneTabs?: { title: string; chips: PhoneCapsule[]; onPick: (id: string) => void };
}) => {
  useBusinessContext(); // keep the provider mounted for downstream consumers
  const isPhone = useIsPhone();
  const [phonePage, setPhonePage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  // Business users (Admin / Front desk) use the shared 3-step sheet; the legacy drawer stays for
  // other groups (property manager, invitation variants…).
  const [businessUserOpen, setBusinessUserOpen] = useState(false);
  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as Business[] | { data?: Business[] } | undefined;
    if (Array.isArray(raw)) return raw;
    return raw && 'data' in raw && Array.isArray(raw.data) ? raw.data : [];
  }, [businessesData]);

  const [selectedUser, setSelectedUser] = useState<SAMUser | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The backend filters server-side by `type`, so only admin users come back.
  const {
    users: adminUsers,
    isLoading: isLoadingAdmin,
    isFetchingMore: isFetchingMoreAdmin,
  } = useAllTenantUsers('admin');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search hook
  const { users: searchResults, isLoading: isSearching } = useSearchUsers(
    debouncedSearch,
    debouncedSearch.length > 0
  );

  // The search endpoint doesn't accept a `type` filter, so keep only admins here.
  const searchAdminUsers = useMemo(
    () => (searchResults || []).filter((user) => user.groups?.some((g) => g.startsWith('business'))),
    [searchResults]
  );

  useEffect(() => { setPhonePage(1); }, [debouncedSearch]);

  const isSearchActive = debouncedSearch.length > 0;
  const displayUsers = isSearchActive ? searchAdminUsers : adminUsers;
  const isLoading = isSearchActive ? isSearching : isLoadingAdmin;
  const isFetchingMore = isSearchActive ? false : isFetchingMoreAdmin;

  // Columns for admin users
  const adminColumns = useMemo<MRT_ColumnDef<SAMUser>[]>(
    () => [
      {
        accessorKey: 'index',
        header: '#',
        size: 60,
        enableSorting: false,
        Cell: ({ row }) => (
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>
            {row.index + 1}
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        Cell: ({ row }) => {
          const user = row.original;
          const displayName =
            user.name && user.family_name
              ? `${user.name} ${user.family_name}`
              : user.name || user.family_name || user.username;
          return (
            <span style={{ fontWeight: 500 }}>
              {displayName}
              {!user.enabled && <DisabledBadge>DISABLED</DisabledBadge>}
            </span>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 250,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return <StatusBadge $status={status}>{status}</StatusBadge>;
        },
      },
      {
        accessorKey: 'groups',
        header: 'Roles',
        size: 250,
        Cell: ({ cell }) => {
          const groups = cell.getValue<string[]>();
          const businessGroups = groups.filter((g) => g.startsWith('business'));
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {businessGroups.slice(0, 3).map((group) => (
                <GroupBadge key={group}>{group.replace('business_', '')}</GroupBadge>
              ))}
              {businessGroups.length > 3 && (
                <GroupBadge>+{businessGroups.length - 3}</GroupBadge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'created',
        header: 'Created',
        size: 130,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue<string>());
          return (
            <span style={{ fontSize: '13px', color: '#6B7280' }}>
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          );
        },
      },
    ],
    []
  );

  const handleRowClick = (user: SAMUser) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  // When the underlying lists refetch (e.g. after disable / enable / role
  // change), find the latest snapshot of the open user and pass *that* to the
  // drawer. Otherwise the drawer keeps showing stale `enabled` / `groups` and
  // the "Delete User" button stays disabled even after the user is disabled.
  const liveSelectedUser = useMemo(() => {
    if (!selectedUser) return null;
    // Lists may not have refetched yet — fall back to the clicked snapshot.
    return adminUsers.find((u) => u.username === selectedUser.username) ?? selectedUser;
  }, [selectedUser, adminUsers]);

  const overlays = (
    <>
        <AddBusinessUserSheet
          isOpen={businessUserOpen}
          onClose={() => setBusinessUserOpen(false)}
          businesses={businesses}
          businessFirst
        />

        <CreateUserDrawer
          isOpen={createDrawerOpen}
          onClose={() => setCreateDrawerOpen(false)}
        />

        <UserDetailDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedUser(null);
          }}
          user={liveSelectedUser}
          onUserUpdated={() => {
            // No-op: the mutation hooks already invalidate ['sam-users'], so
            // `adminUsers` refetches and `liveSelectedUser` picks
            // up the new snapshot. The drawer must stay open so the operator
            // can chain actions (e.g. disable → delete).
          }}
        />
    </>
  );

  // SAM-1308: the phone list (cards, one primary action, a full-width search) instead of the desktop table.
  if (isPhone && phoneTabs) {
    return (
      <>
        <AdminUsersPhone
          title={phoneTabs.title}
          chips={phoneTabs.chips}
          onChip={phoneTabs.onPick}
          users={displayUsers}
          loading={isLoading}
          query={searchTerm}
          onQuery={setSearchTerm}
          page={phonePage}
          onPage={setPhonePage}
          onOpen={handleRowClick}
          onCreate={() => setBusinessUserOpen(true)}
          onOtherRole={() => setCreateDrawerOpen(true)}
        />
        {overlays}
      </>
    );
  }

  const content = (
      <Container>
        <Header>
          {embedded ? <span /> : <HeadingMedium style={{ margin: 0 }}>Admin Users</HeadingMedium>}
          <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={() => setCreateDrawerOpen(true)}>
            Other role…
          </button>
          <Button
            onClick={() => setBusinessUserOpen(true)}
            startEnhancer={() => <UserPlus size={16} />}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #111827)',
                  ':hover': {
                    backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)',
                  },
                },
              },
            }}
          >
            Create business user
          </Button>
          </div>
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <SearchInputEl
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search admin users..."
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#6B7280' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </SearchWrapper>
        </ToolbarRow>

        {isLoading ? (
          <LoadingContainer>
            <Spinner $size={40} />
          </LoadingContainer>
        ) : (
          <Main>
            <MantineDataTable
              columns={adminColumns}
                data={displayUsers}
                loading={false}
                enablePagination
                enableSorting
                enableGlobalFilter={false}
                initialPageSize={20}
                pageSizeOptions={[10, 20, 50, 100]}
                onRowClick={handleRowClick}
              />
            {isFetchingMore && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  color: '#6B7280',
                  fontSize: '12px',
                }}
              >
                <Spinner $size={14} />
                Loading more users…
              </div>
            )}
          </Main>
        )}

        {overlays}
      </Container>
  );

  return embedded ? content : <TableContent>{content}</TableContent>;
};
